import {
  BillingModel as BillingModelProduct,
  Integration,
  MediaType,
  Product,
  ProductMedia,
  ProductStatesModel
} from '@futrli-sdk/app-store';
import { BillingModel, DataSource, Organisation, organisation, ProductSetup } from '@futrli-sdk/organisations';
import { Workspace, OperationType, Permission } from '@futrli-sdk/workspaces';
import { AxiosAdapter } from 'axios';
import { OK } from 'http-status-codes';
import { merge } from 'lodash';
import { normalize, Schema } from 'normalizr';
import { v4 as uuid } from 'uuid';

import { DATASOURCES_STATUS } from '../app-core';
import {
  ActionSet,
  API_REQUEST,
  createActionSet,
  FreeShapeAction,
  REQUEST_METHODS,
  StandardAction
} from '../redux-helpers';

export const WORKSPACE_ID_MOCK = '4d9f49a5-dd76-4d36-ab57-f7108391bad0';

export const createMockResource = (id: string = uuid(), type = 'Organisation') => ({
  id,
  name: `${type}:${id}`,
  created: 1534934369298,
  modified: 1534934369298,
  resource_id: `Workspace::b7bf07f0-56b0-47ad-94f3-c1bee6b62051/${type}::${id}`
});

export const createMockWorkspace = (id: string = uuid(), ownerId: string = uuid()): Workspace =>
  Object.assign(createMockResource(id, 'Workspace'), {
    owner: {
      id: ownerId,
      sageid_id: '',
      first_name: 'user'
    },
    users: [],
    subscription_state: '',
    consolidation_permission: 'allowed' as 'allowed' | 'not_allowed'
  });

export const createMockOrg = (id: string = uuid()): Organisation =>
  Object.assign(createMockResource(id, 'Organisation'), {
    syncing_enabled: false,
    image: '',
    workspace_id: `workspace_id::${id}`,
    resource_id: '',
    datasource: createMockDataSource('0', 'Xero'),
    legacy_deactivated_at: '',
    created: '2222',
    modified: '2222',
    can_be_accessed: true,
    billed_by_status: 'active' as
      | 'active'
      | 'blocked'
      | 'cancelled'
      | 'expired'
      | 'nolicense'
      | 'pending'
      | 'reset'
      | 'not_active'
      | null,
    billed_by: 'chargebee' as 'chargebee' | 'sage50' | 'not_billed' | null,
    kind: null,
    enabled_products: [
      {
        name: 'Enabled Product 1',
        created: '1970-01-01T00:00:00.000Z',
        id: 'product-1',
        image: 'https://s3-eu-west-1.amazonaws.com/futrli-api-example-data/datasources/xero.png',
        launch_url: 'http://google.co.uk',
        billing_model: BillingModel.free,
        is_pro: false,
        product_id: 'product_id-1',
        state: ProductStatesModel.active,
        kind: null
      },
      {
        name: 'Enabled Product 2',
        created: '1970-01-01T00:00:00.000Z',
        id: 'product-2',
        image: 'https://s3-eu-west-1.amazonaws.com/futrli-api-example-data/datasources/xero.png',
        launch_url: 'http://google.co.uk',
        billing_model: BillingModel.premium,
        is_pro: false,
        product_id: 'product_id-2',
        state: ProductStatesModel.inactive,
        kind: null
      },
      {
        name: 'Enabled Product 3',
        created: '1970-01-01T00:00:00.000Z',
        id: 'product-3',
        image: 'https://s3-eu-west-1.amazonaws.com/futrli-api-example-data/datasources/xero.png',
        launch_url: 'http://google.co.uk',
        billing_model: BillingModel.freemium,
        is_pro: false,
        product_id: 'product_id-3',
        state: ProductStatesModel.hidden,
        kind: null
      }
    ],
    links: [],
    can_be_added_to_consolidation: true
  });

export const createMockProduct = (id: string = uuid(), slug = 'productSlug'): Product =>
  Object.assign(createMockResource(id, 'Product'), {
    slug,
    image: 'lol.jpg',
    billing_model: BillingModelProduct.free,
    media: [],
    description: 'A description',
    status: ProductStatesModel.active,
    branding: {
      colours: {
        primary: ''
      }
    }
  }) as Product;

export const createMockProductSetup = (id: string = uuid()): ProductSetup =>
  Object.assign(createMockResource(id, 'ProductSetup'), {
    image: 'lol.jpg',
    created: '1970-01-01T00:00:00.000Z',
    billing_model: BillingModel.free,
    media: [],
    description: 'A description',
    product_id: `product_id::${id}`,
    launch_url: `http://launch_url`,
    is_pro: false
  });

export const createMockIntegration = (id: string = uuid(), tags: string[] = []): Integration =>
  Object.assign(createMockResource(id, 'Integration'), {
    tags,
    name: 'Xero',
    image: 'https://s3-eu-west-1.amazonaws.com/futrli-api-example-data/datasources/xero.png',
    source_type: '',
    products: [],
    pricing: {
      id: '',
      name: '',
      amounts: [
        {
          currency: 'GBP',
          monthly_recurring_amount: 30
        }
      ]
    },
    description: 'An integration',
    slug: 'slug',
    launch_url: 'http://google.co.uk/'
  });

export const createMockDataSource = (
  id: string = uuid(),
  type = 'Xero',
  statusValue: string = DATASOURCES_STATUS.sync_start,
  integrationId?: string
): DataSource =>
  Object.assign(createMockResource(id, 'DataSource'), {
    integration_id: integrationId || `integration_id::${id}`,
    currency_code: 'EUR',
    parent_datasource_id: !id ? uuid() : `parent_datasource_id::${id}`,
    name: `Grahams Gin - ${type} Organisation`,
    type: `${type.toLowerCase()}`,
    status: {
      value: statusValue,
      description: '',
      modified: '2019-03-29T10:28:06.304Z',
      created: '2019-05-01T11:00:06.304Z'
    },
    sync_history: [],
    image: `https://s3-eu-west-1.amazonaws.com/futrli-api-example-data/datasources/${type.toLowerCase()}.png`
  });

export const createMockProductMedia = (id: string = uuid(), mediaType: MediaType = MediaType.video): ProductMedia => ({
  url: `http://url_${id}`,
  type: mediaType,
  description: 'Media Type description'
});

export const mockWorkspaces: Workspace[] = [
  createMockWorkspace(WORKSPACE_ID_MOCK, 'owner-a'),
  createMockWorkspace('workspace-1', 'owner-a'),
  createMockWorkspace('workspace-2', 'owner-a')
];

export const mockIntegrations: Integration[] = [
  createMockIntegration('integration-1'),
  createMockIntegration('integration-2'),
  createMockIntegration('integration-3')
];

export const mockOrganisations: Organisation[] = [
  createMockOrg('org-1'),
  createMockOrg('org-2'),
  createMockOrg('org-3'),
  createMockOrg('org-4'),
  createMockOrg('org-5')
];

export const mockProducts: Product[] = [
  createMockProduct('product-1'),
  createMockProduct('product-2'),
  createMockProduct('product-3')
];

export const mockDataSource: DataSource[] = [
  createMockDataSource('dataSource-1'),
  createMockDataSource('dataSource-2'),
  createMockDataSource('dataSource-3')
];

export const MOCK_ORGANISATIONS_PATH = '/organisations/';

export const mockFetchOrgsSet: ActionSet = createActionSet('FETCH_ORGANISATIONS');

export const mockOrgsSchema: Schema = [organisation];

export const mockNormalisedOrganisations = normalize(mockOrganisations, mockOrgsSchema);

/**
 * Creates a fetch mock organisations action for the Redux API Middleware.
 * @param {AxiosAdapter} mockAdapter
 * @param {string} client
 * @param {ActionSet} actionSet
 * @param {Schema} schema
 * @returns API request action
 */
export const createMockFetchOrgsAction = (
  mockAdapter: AxiosAdapter,
  client: string | null = null,
  actionSet: ActionSet = mockFetchOrgsSet,
  schema: Schema = mockOrgsSchema
): StandardAction => {
  const data = {
    type: actionSet.ACTION,
    payload: {
      request: {
        url: MOCK_ORGANISATIONS_PATH,
        adapater: mockAdapter
      }
    },
    meta: {
      schema,
      flag: API_REQUEST
    }
  };

  return !client ? data : merge({}, data, { payload: { client } });
};

/**
 * Creates a fetch mock organisations for the API Client.
 * @param {AxiosAdapter} mockAdapter
 * @param {Schema} schema
 * @returns API request
 */
export const createMockFetchOrgsRequest = (mockAdapter: AxiosAdapter, schema: Schema = mockOrgsSchema) => ({
  payload: {
    request: {
      method: REQUEST_METHODS.GET,
      url: MOCK_ORGANISATIONS_PATH,
      adapater: mockAdapter
    }
  },
  meta: {
    schema
  }
});

export const mockSuccessExpectActions: FreeShapeAction[] = [
  {
    type: mockFetchOrgsSet.PENDING,
    meta: {
      schema: mockOrgsSchema
    }
  },
  {
    type: mockFetchOrgsSet.FULFILLED,
    payload: {
      data: mockNormalisedOrganisations,
      headers: {},
      request: {
        responseUrl: '/organisations/'
      },
      status: OK,
      config: {}
    },
    meta: {
      schema: mockOrgsSchema
    }
  }
];

export const mockRejectedExpectActions: FreeShapeAction[] = [
  {
    type: mockFetchOrgsSet.PENDING,
    meta: {
      schema: mockOrgsSchema
    }
  },
  {
    type: mockFetchOrgsSet.REJECTED,
    payload: new Error('Request failed with status code 404'),
    error: true,
    meta: {
      schema: mockOrgsSchema
    }
  }
];

export const createMockWorkspacePermission = (
  slug: string,
  operations: (OperationType.read | OperationType.update)[]
): Permission => ({
  operations,
  slug,
  id: slug
});

export const createMockFullWorkspacePermissions = (): Permission[] => {
  return [
    {
      id: 'orgs',
      operations: ['update'],
      slug: 'organisations'
    },
    {
      id: 'roles',
      operations: ['update'],
      slug: 'roles'
    },
    {
      id: 'users',
      operations: ['update'],
      slug: 'members'
    }
  ];
};
